/**
 * F 378 — RUN THE VIEW MIGRATION ONCE PER DATABASE, FROM THE UI.
 *
 * WHY A BOOT HOOK RATHER THAN A SCRIPT
 * -----------------------------------
 * The views live in the BROWSER's localStorage, per user. There is no
 * server-side script that can reach them, and no batch job that can run
 * "for every user" — so "run the migration" necessarily means running it in
 * the user's own browser, once, the first time a database view mounts.
 *
 * WHY IT IS SAFE TO DO THAT
 * ------------------------
 * `copyLegacyViewsToCore` is copy-only: it never deletes the legacy key, and
 * it refuses to overwrite an already-populated core copy. So the worst outcome
 * of running this on every mount is "nothing happens", not "the user's views
 * are gone".
 *
 * The failure modes are all silent-safe by construction:
 *   - no legacy data       -> no_legacy, nothing written
 *   - already migrated     -> skipped, nothing written
 *   - unparseable legacy   -> blob left untouched for a human
 *   - adapter write fails  -> verified:false, legacy still there to retry
 *
 * NOTHING HERE BLOCKS RENDER. The caller fires and forgets; the UI reads the
 * LEGACY key for its own state either way, so a failed migration cannot break
 * the database view. That ordering is deliberate: the copy populates the graph
 * for the agent to read, it does not become a dependency of the UI.
 */
import { LocalStorageAdapter } from '../storage/adapters/LocalStorageAdapter';
import { copyLegacyViewsToCore, type CopyMigrationResult } from './viewConfig';
import type { StorageAdapter } from '../storage/types';

/**
 * Databases already attempted in this page session.
 *
 * `copyLegacyViewsToCore` is already idempotent against storage, so this is
 * not about correctness — it is about not re-running a storage round-trip on
 * every re-render or view switch.
 */
const attempted = new Set<string>();

/** Exposed for tests: clear the per-session memo. */
export function __resetViewMigrationAttempts(): void {
  attempted.clear();
}

/**
 * Copy `freshcards:views:<dbId>` to `view:<dbId>` once per database per session.
 *
 * @returns the result, or null when it had already been attempted this session.
 */
export async function runViewMigrationOnBoot(
  databaseId: string,
  adapter?: StorageAdapter,
): Promise<CopyMigrationResult | null> {
  if (!databaseId || typeof localStorage === 'undefined') return null;
  if (attempted.has(databaseId)) return null;
  attempted.add(databaseId);

  // Cheap pre-check: no legacy key means nothing to do, and this avoids
  // constructing an adapter (and touching storage) on every fresh database.
  if (localStorage.getItem(`freshcards:views:${databaseId}`) === null) {
    return {
      databaseId, copied: 0,
      from: `freshcards:views:${databaseId}`, to: `view:${databaseId}`,
      verified: false, skipped: false, reason: 'no_legacy', legacyRemoved: false,
    };
  }

  const store = adapter ?? new LocalStorageAdapter();
  try {
    const res = await copyLegacyViewsToCore(store, databaseId);
    // One line, on the record. Silent migrations are how "the AI can't see my
    // views" stays a mystery for months.
    console.info(
      `[views] migration ${databaseId}: copied=${res.copied} verified=${res.verified} ` +
      `skipped=${res.skipped} reason=${res.reason ?? 'ok'} legacyRemoved=${res.legacyRemoved}`,
    );
    return res;
  } catch (e) {
    // A throw here must never take the UI down with it.
    console.warn('[views] migration failed for', databaseId, e);
    return null;
  }
}